import { useQuery } from "@tanstack/react-query";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { tonePillClass } from "@/lib/tone";
import {
  freshnessBoundMonth,
  freshnessDate,
  partialMonths,
  selectGrainSeries,
  type RangeValue,
} from "@/lib/time-range";
import { DataIssueBanner, UpdatedStamp } from "@/components/data";

import { getRegion, messageFor, type Region, type RegionDetail } from "./api";
import { CampaignTable } from "./components/CampaignTable";
import { CostStackPanel } from "./components/CostStackPanel";
import { YieldHeadline } from "./components/YieldHeadline";
import { YieldTrend } from "./components/YieldTrend";
import { YieldControls, useControls } from "./controls";
import { freshnessState, regionLabel, type Grain } from "./format";
import { headlineWindow, windowBucketSet, windowCampaigns, windowLabel } from "./window";

export function RegionTab({ region, areaName }: { region: Region; areaName: string }) {
  const controls = useControls();

  const detail = useQuery({
    queryKey: ["meteoric_profit_yield", "region", region],
    queryFn: () => getRegion(region),
  });

  if (detail.isPending) return <Skeleton className="h-96 w-full" />;

  if (detail.isError) {
    return (
      <Alert>
        <AlertTitle>Profit Yield {regionLabel(region)} unavailable</AlertTitle>
        <AlertDescription>{messageFor(detail.error, areaName)}</AlertDescription>
      </Alert>
    );
  }

  const { tile, secondary } = detail.data.data;
  const dataThrough = detail.data.meta.data_as_of;
  const state = freshnessState(dataThrough);
  const reference = freshnessDate(dataThrough);
  // W1: see OverviewTab - the flattened `series` is truer than a monthly
  // series that has not reached last month's close.
  const monthlyBound = freshnessBoundMonth(reference);
  const monthlyReaches =
    (tile.series_monthly?.length ?? 0) > 0 &&
    (tile.series_monthly.at(-1)?.m ?? "") >= monthlyBound;
  // Raw (untrimmed) - see OverviewTab.
  const rawMonthly = (monthlyReaches ? tile.series_monthly : tile.series).filter((p) => p.present);
  const points = selectGrainSeries(
    {
      daily: tile.series_daily,
      weekly: tile.series_weekly,
      monthly: monthlyReaches ? tile.series_monthly : tile.series,
    },
    controls.grain,
    controls.range,
    reference,
  ).filter((p) => p.present);
  const head = headlineWindow(tile, points, controls.grain, controls.range, reference);
  const table = campaignTable(detail.data.data, controls.grain, controls.range, reference, head.window?.buckets);
  const provisional = table.fromProducer && secondary.maturity !== "closed";

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <YieldControls controls={controls} />
        <UpdatedStamp generatedAt={detail.data.data.generated_at} />
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="font-medium text-ks-ink">Data through {dataThrough ?? "-"}</span>
        <Badge variant="outline" className={tonePillClass(state.tone)}>
          {state.word}
          {state.ageLabel ? ` - ${state.ageLabel}` : ""}
        </Badge>
      </div>

      <DataIssueBanner issues={controls.grain === "monthly" ? partialMonths(rawMonthly, reference) : []} />

      <YieldHeadline
        tile={tile}
        secondary={secondary}
        trend={<YieldTrend points={points} grain={controls.grain} reference={reference} />}
        window={head.window}
        note={head.note}
      />

      <CostStackPanel controls={controls} region={region} areaName={areaName} />

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-ks-ink">
                Campaign verdicts{table.label ? ` (${table.label})` : ""}
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">{table.caption}</p>
            </div>
            {/* An open month's verdicts are not verdicts. Saying so beside the
                table is the difference between a reader cutting a campaign and
                a reader waiting a week. */}
            {provisional && <Badge variant="outline">Verdicts are computed on closed periods only</Badge>}
          </div>

          <CampaignTable
            campaigns={table.campaigns}
            tail={table.tail}
            region={regionLabel(region)}
            values={controls.values}
            onChange={controls.onChange}
          />
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * The campaign table for the page's window (KET-6591). Daily and weekly read
 * the matching per-(bucket, campaign) rows; a monthly window other than the
 * producer's own month reads the daily rows inside those months. The producer's
 * month - or an artefact without grain rows - keeps the producer's own ranked
 * table and its closed-period verdicts, and the caption says which one this is.
 */
function campaignTable(
  data: RegionDetail,
  grain: Grain,
  range: RangeValue,
  reference: Date,
  windowBuckets: string[] | undefined,
) {
  const producer = {
    campaigns: data.campaigns,
    tail: data.tail,
    label: data.tile.period ?? "",
    caption: "The producer's top campaigns by spend for the latest closed month.",
    fromProducer: true,
  };
  const monthly = grain === "monthly";
  if (monthly && !windowBuckets) return producer;
  const rows = (grain === "weekly" ? data.campaigns_weekly : data.campaigns_daily) ?? [];
  if (rows.length === 0) {
    return { ...producer, caption: `${producer.caption} Campaign detail for the selected range is not available yet.` };
  }
  const rowGrain = monthly ? "daily" : grain;
  const buckets = windowBucketSet(rows, rowGrain, range, reference, monthly ? new Set(windowBuckets) : undefined);
  const windowed = windowCampaigns(rows, buckets);
  if (!windowed.available) {
    return { ...producer, caption: `${producer.caption} No campaign spend in the selected range.` };
  }
  return {
    campaigns: windowed.campaigns,
    tail: windowed.tail,
    label: windowLabel(monthly ? "monthly" : grain, monthly ? (windowBuckets ?? []) : buckets),
    caption: "Top campaigns by spend in the selected range, with verdicts on the range's own totals.",
    fromProducer: false,
  };
}
